import { z } from 'zod';
import { uuidSchema } from '../common';
import { AGENT_ROLES, type AgentRole } from '../../constants';
import { SUMMARIZATION_THRESHOLD_MIN, SUMMARIZATION_THRESHOLD_MAX } from '../../summarizer';
import { PREVIOUS_CONVERSATIONS_MAX } from '../../scheduling/placeholders';

// Re-exported beside the field it bounds, so a consumer of the contract gets the limit
// and the clamp with it rather than restating either. The constant's own home
// (`scheduling/placeholders`) has no export subpath, and the root `@wayai/core` barrel
// re-exports `./observability`, which needs `node:async_hooks` — Turbopack rejects that
// in a client bundle (see the note above the core imports in AgentDetailView.tsx).
export { PREVIOUS_CONVERSATIONS_MAX };

/**
 * The single rounding + bounding rule for `previous_conversations_count`.
 *
 * Every write surface needs it — REST via the Zod field below, CI create and update
 * (which carry no Zod), and the settings input — and the READ side in ConversationDO
 * clamps again as defence in depth. Five independent expressions is how they end up
 * disagreeing about a fractional value, so there is one.
 *
 * Returns null for "off", which is what both 0 and null mean at the column.
 */
export function clampPreviousConversationsCount(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.min(PREVIOUS_CONVERSATIONS_MAX, Math.max(0, Math.round(value)));
  return rounded > 0 ? rounded : null;
}

// Relocated from the hub (`updateHubBody`) to the summarizer agent — preserve the
// original int+range guard on the write paths that set it. `.passthrough()` keeps
// other agent keys flowing; this explicit key restores validation for this one.
// How many of the user's prior ended conversations this agent receives as context.
// A real `agent` column, so `.passthrough()` plus HubDO's column-derived writer would
// otherwise persist ANY value here — `"garbage"` included, which the turn path reads
// back as NaN and silently treats as off. Validated at the contract, where a bad
// write is a 400 the caller can see. 0 and null both mean off.
const previousConversationsCountField = z
  .number()
  .int()
  .min(0)
  .max(PREVIOUS_CONVERSATIONS_MAX)
  .nullable()
  .optional();

const summarizationThresholdField = z
  .number()
  .int()
  .min(SUMMARIZATION_THRESHOLD_MIN)
  .max(SUMMARIZATION_THRESHOLD_MAX)
  .nullable()
  .optional();

// Relocated from the hub (`updateHubBody`) to the monitor agent — restore the
// guards the hub UI enforced (`delay_seconds >= 10`) on every write path so a caller
// (REST/CLI/MCP) can't persist an invalid monitor schedule. JSON object on the monitor
// agent column; `.passthrough()` keeps other agent keys flowing. Meaningful only on
// `monitor` agents. `flag_conditions` mirrors the FlagCondition shape used elsewhere.
const monitorConfigField = z
  .object({
    delay_seconds: z.number().int().min(10),
    flag_conditions: z
      .array(
        z.object({
          variable: z.string(),
          operator: z.enum(['=', '!=']),
          value: z.union([z.string(), z.number()]),
        }),
      )
      .optional(),
  })
  .passthrough()
  .nullable()
  .optional();

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const agentIdParam = z.object({
  id: uuidSchema,
});

export const listAgentsQuery = z.object({
  hub_id: uuidSchema,
});

export const agentConnectionsQuery = z.object({
  hub_id: uuidSchema,
});

export const createAgentBody = z.object({
  agent_name: z.string().min(1),
  hub_id: uuidSchema,
  agent_role: z.enum(AGENT_ROLES),
  additional_context_template: z.string().max(20000).optional(),
  summarization_threshold_tokens: summarizationThresholdField,
  previous_conversations_count: previousConversationsCountField,
  monitor_config: monitorConfigField,
}).passthrough();

export const updateAgentBody = z.object({
  additional_context_template: z.string().max(20000).optional(),
  summarization_threshold_tokens: summarizationThresholdField,
  previous_conversations_count: previousConversationsCountField,
  monitor_config: monitorConfigField,
}).passthrough();

// -- Agent parameters (a.k.a. evaluation variables) ---------------------------

export const AGENT_PARAMETER_TYPES = ['string', 'number', 'boolean', 'integer', 'enum'] as const;

// Mirror of the backend `VALID_VARIABLE_NAME_REGEX`
// (apps/backend/src/clickhouse/types.ts) — kept in sync here (same pattern as
// slug.ts) because the contract can't import from apps/backend. The name becomes
// a ClickHouse analytics path segment (data.variables.<name> /
// data.eval_scores.<name>), so a name the analytics layer would silently drop
// must be rejected at the write edge, not persisted.
export const AGENT_PARAMETER_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/;

/**
 * One agent parameter on create. `.strict()` rejects unknown keys — the
 * `agent_parameter` table has a fixed column set, so there's nothing to pass
 * through (unlike the agent row, which uses `.passthrough()`). Validates the
 * per-field shape plus the `parameter_name`/`parameter_type` guards the CI write
 * path enforces (`normalizeYamlEvalVars` in routes/api/ci/agent-parameters.ts), so
 * the REST path can't persist a malformed/analytics-breaking parameter. The CI
 * path's cross-field invariants (enum requires values, is_summary only for
 * strings, no duplicate names in a batch) are intentionally out of scope here —
 * the read path (`normalizeDbEvalVars`) already degrades those gracefully. (gh #2870)
 */
export const agentParameterCreateSchema = z
  .object({
    parameter_name: z
      .string()
      .regex(
        AGENT_PARAMETER_NAME_REGEX,
        'parameter_name must start with a letter or underscore and contain only letters, digits, and underscores (max 64 chars)',
      ),
    parameter_type: z.enum(AGENT_PARAMETER_TYPES).optional(),
    // No length caps on the free-text fields below: the agent_parameter columns are
    // unbounded TEXT and pre-PR rows were never length-validated, so a cap would 422
    // an unrelated GET→edit→re-save of legitimately-long existing data. The name
    // regex (above) and the batch cap (below) are the load-bearing guards; total
    // payload size belongs in a global /api/* body limit, not per field here.
    parameter_description: z.string().nullable().optional(),
    parameter_label: z.string().nullable().optional(),
    parameter_enum: z.array(z.string()).nullable().optional(),
    parameter_required: z.boolean().optional(),
    // Free-form analytics grouping label — no DB CHECK and the CI path persists
    // arbitrary strings, so keep this permissive (not the 4-value UI dropdown enum).
    variable_category: z.string().nullable().optional(),
    is_summary: z.boolean().optional(),
  })
  .strict();

// Batch cap: the body is forwarded element-by-element into the single-threaded
// HubDO write loop (one INSERT + one SELECT each), and /api/* carries no bodyLimit
// middleware — an unbounded array would let one hub:write caller drive an O(n)
// write storm. A hub agent has at most a few dozen parameters in practice.
export const MAX_AGENT_PARAMETERS_PER_REQUEST = 100;

/**
 * POST body for `POST /api/setup/agents/:id/parameters`. Accepts a single
 * parameter or a batch — the frontend always sends an array; `HubDO.createAgentParameter`
 * accepts both, so preserve that dual contract.
 */
export const createAgentParametersBody = z.union([
  agentParameterCreateSchema,
  z
    .array(agentParameterCreateSchema)
    .max(MAX_AGENT_PARAMETERS_PER_REQUEST, `too many parameters (max ${MAX_AGENT_PARAMETERS_PER_REQUEST})`),
]);

// =============================================================================
// INFERRED TYPES (for consumers that need request types without importing zod)
// =============================================================================

export type CreateAgentInput = z.infer<typeof createAgentBody>;
export type CreateAgentParameterInput = z.infer<typeof agentParameterCreateSchema>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** Full agent row as returned by select('*') on the agent table */
export interface AgentResponse {
  agent_id: string;
  agent_name: string;
  created_at: string;
  agent_sid: string | null;
  instructions: string;
  response_format: string;
  updated_at: string | null;
  hub_id: string;
  connection_id: string;
  agent_role: AgentRole;
  schema_json: Record<string, unknown> | null;
  schema_name: string | null;
  agent_settings: Record<string, unknown> | null;
  instructions_file_id: string | null;
  additional_context_template: string;
  enabled: boolean;
  include_message_timestamps: boolean;
  auto_format: boolean;
  /** Conversation-flagging conditions, evaluated post-close by the
   *  `conversation_evaluator` agent (relocated from the hub column). Only
   *  meaningful on `conversation_evaluator` agents; null/omitted elsewhere. */
  flag_conditions: Array<{ variable: string; operator: '=' | '!='; value: string | number }> | null;
  /** Token threshold that fires the rolling summarizer (sum of input + cache_read +
   *  cache_creation). A `summarizer` agent setting (relocated from the hub column).
   *  Only meaningful on `summarizer` agents; null/omitted elsewhere → falls back to
   *  DEFAULT_SUMMARIZATION_THRESHOLD_TOKENS at the read site. */
  summarization_threshold_tokens?: number | null;
  /** How many of the user's most recent ended conversations this agent receives as
   *  context, injected into the conversation's first user message and frozen there.
   *  Null/0 = off. Ignored on background roles. Bounded by
   *  `PREVIOUS_CONVERSATIONS_MAX`. */
  previous_conversations_count?: number | null;
  /** Monitor scheduling + flagging config ({delay_seconds, flag_conditions}). A
   *  `monitor` agent setting (relocated from the hub column). Only meaningful on
   *  `monitor` agents; null/omitted elsewhere. The monitor is NOT auto-provisioned. */
  monitor_config?: {
    delay_seconds: number;
    flag_conditions?: Array<{ variable: string; operator: '=' | '!='; value: string | number }>;
  } | null;
}

/** Agent parameter row */
export interface AgentParameterResponse {
  agent_parameter_id: string;
  created_at: string;
  agent_id: string;
  parameter_name: string;
  parameter_type: 'string' | 'number' | 'boolean' | 'integer' | 'enum';
  parameter_description: string;
  parameter_label: string | null;
  parameter_enum: string[] | null;
  parameter_required: boolean;
  variable_category: 'quality' | 'satisfaction' | 'compliance' | 'categorization' | null;
  is_summary: boolean;
}

/** Simplified agent connection detail returned by fetchAgentById */
export interface AgentConnectionDetail {
  connection_id: string;
  connection_display_name: string | null;
  connector_id: string;
  agent_settings_schema: Record<string, unknown> | null;
}

/** Connection with nested connector, returned by the /connections endpoint */
export interface AgentConnectionWithConnector {
  connection_id: string;
  created_at: string;
  service_name: string | null;
  api_key: string | null;
  updated_at: string | null;
  hub_id: string;
  access_token: string | null;
  access_token_expires_at: string | null;
  access_token_secret_id: string | null;
  account_sid: string | null;
  api_key_secret_id: string | null;
  authentication_type: string;
  enabled: boolean;
  connector_id: string;
  connector_name: string;
  password: string | null;
  password_secret_id: string | null;
  connection_display_name: string | null;
  connector_type: string;
  refresh_token: string | null;
  refresh_token_expires_at: string | null;
  refresh_token_secret_id: string | null;
  state: Record<string, unknown> | null;
  username: string | null;
  base_url: string | null;
  connection_headers: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  token_refresh_config: Record<string, unknown> | null;
  next_access_token_refresh_at: string | null;
  next_refresh_token_renewal_at: string | null;
  last_token_refresh_at: string | null;
  last_token_refresh_error: string | null;
  organization_credential_id: string | null;
  connector: {
    connector_id: string;
    created_at: string;
    service_name: string;
    authentication_type: string;
    init_endpoint: string | null;
    connector_type: string;
    connector_name: string;
    unique_per_hub: boolean;
    account_sid_placeholder: string | null;
    models: string[] | null;
    is_internal: boolean;
    connector_module: string | null;
    enabled: boolean;
    connector_settings_schema: Record<string, unknown> | null;
    agent_settings_schema: Record<string, unknown> | null;
    channel_settings_schema: Record<string, unknown> | null;
    tool_settings_schema: Record<string, unknown> | null;
    connector_credential_schema: Record<string, unknown> | null;
    token_refresh_config: Record<string, unknown>;
    connector_description: string | null;
  } | null;
}

// -- Endpoint response wrappers --

export interface ListAgentsResponse {
  data: AgentResponse[];
}

export interface GetAgentResponse {
  data: {
    agent: AgentResponse;
    agent_connections: AgentConnectionDetail[];
    agent_parameters: AgentParameterResponse[];
  };
}

export interface CreateAgentResponse {
  data: AgentResponse[];
}

export interface UpdateAgentResponse {
  data: AgentResponse[];
}

export interface ListAgentConnectionsResponse {
  data: AgentConnectionWithConnector[];
}

export interface ListAgentParametersResponse {
  data: AgentParameterResponse[];
}

export interface CreateAgentParametersResponse {
  data: AgentParameterResponse[];
}
